import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import type { Actuacion, Documento, Parte, TipoActuacion, Usuario } from "../../../shared/types";
import { fechaHoyEnZonaSistema } from "../../../shared/timezone";
import { useAuth } from "../../auth/AuthContext";
import { Boton } from "../../components/Boton";
import { Campo, Input, Select, TextArea } from "../../components/Formulario";
import { Insignia } from "../../components/Insignia";
import { Modal } from "../../components/Modal";
import { Tabla, type ColumnaTabla } from "../../components/Tabla";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";

// Tipos de actuación que usan el sub-bloque "Parte vinculada / Lugar o
// enlace / Monto ofrecido / Monto acordado" — mismos valores sembrados en
// TIPOS_ACTUACION_POR_DEFECTO (functions/_app/lib/catalogos-estudio.ts) y
// en migrations/0021_accidentes_transito.sql para el flujo de Accidentes
// de Tránsito. Para cualquier otro tipo (Escrito, Audiencia, Presentación,
// Diligencia, Notificación, Reunión, Otro) ese sub-bloque queda oculto —
// "Adjuntar documento" y "Generar vencimiento" NO dependen de esto, se
// usan seguido en cualquier tipo de actuación.
const TIPOS_CON_DATOS_OCC = new Set([
  "Audiencia OCC",
  "Cédula de Notificación",
  "Pericia",
  "Oficio",
  "Testigo",
]);

export function PestanaActuaciones({
  expedienteId,
  actuaciones,
  partes,
  documentos,
  onCambio,
}: {
  expedienteId: string;
  actuaciones: Actuacion[];
  partes: Parte[];
  documentos: Documento[];
  onCambio: () => void;
}) {
  const { mostrar } = useToast();
  const { usuario } = useAuth();
  const [modalAbierto, setModalAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [tiposActuacion, setTiposActuacion] = useState<TipoActuacion[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [generarVencimiento, setGenerarVencimiento] = useState(false);
  const [valores, setValores] = useState({
    tipo: "",
    fecha: fechaHoyEnZonaSistema(),
    descripcion: "",
    estado: "Realizada",
    prioridad: "Media",
    responsable_id: usuario?.id ?? "",
  });
  // Datos adicionales reutilizados por audiencias OCC, notificaciones/
  // cédulas, pericias, oficios y testigos — siempre visibles y opcionales,
  // sin lógica condicional por tipo (ver migrations/0021_accidentes_transito.sql).
  const [parteId, setParteId] = useState("");
  const [lugarOEnlace, setLugarOEnlace] = useState("");
  const [montoOfrecido, setMontoOfrecido] = useState("");
  const [montoAcordado, setMontoAcordado] = useState("");
  const [vencimiento, setVencimiento] = useState({
    tipo_evento: "Vencimiento Procesal",
    fecha_vencimiento: fechaHoyEnZonaSistema(),
    responsable_id: usuario?.id ?? "",
    prioridad: "Media",
  });

  const [enEdicion, setEnEdicion] = useState<Actuacion | null>(null);
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);

  useEffect(() => {
    api
      .get<{ items: TipoActuacion[] }>("/catalogos-estudio/tipos-actuacion")
      .then((d) => setTiposActuacion(d.items.filter((t) => t.activo)))
      .catch(() => undefined);
    // Algunos roles no tienen usuarios.ver por defecto — si falla, se arma
    // un padrón de responsables asignables a partir de GET /actuaciones
    // (mismo criterio que en PestanaAgenda.tsx), que sólo exige
    // actuaciones.ver.
    api
      .get<{ items: Usuario[] }>("/usuarios")
      .then((d) => setUsuarios(d.items))
      .catch(() => {
        api
          .get<{ items: Actuacion[] }>("/actuaciones")
          .then((d) => {
            const vistos = new Map<string, string>();
            for (const a of d.items) {
              if (a.responsable_id && a.responsable_nombre) {
                vistos.set(a.responsable_id, a.responsable_nombre);
              }
            }
            setUsuarios(
              Array.from(vistos, ([id, nombre_completo]) => ({ id, nombre_completo }) as Usuario).sort((a, b) =>
                a.nombre_completo.localeCompare(b.nombre_completo),
              ),
            );
          })
          .catch(() => undefined);
      });
  }, []);

  const mostrarDatosOcc = TIPOS_CON_DATOS_OCC.has(valores.tipo);
  const mostrarDatosOccEdicion = enEdicion ? TIPOS_CON_DATOS_OCC.has(enEdicion.tipo) : false;

  function actualizar(campo: string, valor: string | boolean) {
    setValores((v) => ({ ...v, [campo]: valor }));
    // Al cambiar a un tipo que ya no muestra el sub-bloque OCC, se limpian
    // esos campos para no enviar datos "fantasma" de una selección previa
    // que el usuario ya no ve en pantalla.
    if (campo === "tipo" && !TIPOS_CON_DATOS_OCC.has(valor as string)) {
      setParteId("");
      setLugarOEnlace("");
      setMontoOfrecido("");
      setMontoAcordado("");
    }
  }

  function manejarArchivo(e: ChangeEvent<HTMLInputElement>) {
    setArchivo(e.target.files?.[0] ?? null);
  }

  async function manejarEnvio(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      const cuerpo: Record<string, unknown> = {
        expediente_id: expedienteId,
        ...valores,
        parte_id: parteId || null,
        lugar_o_enlace: lugarOEnlace || null,
        monto_ofrecido: montoOfrecido ? Number(montoOfrecido) : null,
        monto_acordado: montoAcordado ? Number(montoAcordado) : null,
      };
      if (generarVencimiento) {
        cuerpo.vencimiento = vencimiento;
      }
      const respuesta = await api.post<{ actuacion: Actuacion }>("/actuaciones", cuerpo);

      if (archivo) {
        const formData = new FormData();
        formData.append("archivo", archivo);
        formData.append("titulo", archivo.name);
        formData.append("expediente_id", expedienteId);
        formData.append("actuacion_id", respuesta.actuacion.id);
        await api.subirArchivo("/documentos", formData).catch(() => {
          mostrar("La actuación se guardó, pero no se pudo adjuntar el documento.", "error");
        });
      }

      mostrar("Actuación registrada.", "exito");
      setModalAbierto(false);
      setArchivo(null);
      setGenerarVencimiento(false);
      setParteId("");
      setLugarOEnlace("");
      setMontoOfrecido("");
      setMontoAcordado("");
      onCambio();
    } catch (err) {
      mostrar(
        err instanceof ErrorApi
          ? err.message
          : "No se pudo registrar la actuación.",
        "error",
      );
    } finally {
      setGuardando(false);
    }
  }

  function abrirEdicion(a: Actuacion) {
    setEnEdicion(a);
  }

  async function guardarEdicion(e: FormEvent) {
    e.preventDefault();
    if (!enEdicion) return;
    setGuardandoEdicion(true);
    try {
      await api.put(`/actuaciones/${enEdicion.id}`, {
        tipo: enEdicion.tipo,
        fecha: enEdicion.fecha,
        descripcion: enEdicion.descripcion,
        prioridad: enEdicion.prioridad,
        responsable_id: enEdicion.responsable_id || null,
        parte_id: enEdicion.parte_id || null,
        lugar_o_enlace: enEdicion.lugar_o_enlace || null,
        monto_ofrecido: enEdicion.monto_ofrecido,
        monto_acordado: enEdicion.monto_acordado,
      });
      mostrar("Actuación actualizada.", "exito");
      setEnEdicion(null);
      onCambio();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo actualizar la actuación.", "error");
    } finally {
      setGuardandoEdicion(false);
    }
  }

  const columnas: ColumnaTabla<Actuacion>[] = [
    { encabezado: "Fecha", render: (a) => a.fecha },
    { encabezado: "Tipo", render: (a) => a.tipo },
    { encabezado: "Descripción", render: (a) => a.descripcion },
    {
      encabezado: "Prioridad",
      render: (a) => <Insignia texto={a.prioridad} tipo="prioridad" />,
    },
    {
      encabezado: "Documento",
      // El vínculo documentos.actuacion_id ya existía, pero era invisible:
      // ni la pestaña Actuaciones mostraba si había un archivo adjunto, ni
      // Documentos mostraba a qué actuación pertenecía (ver PestanaDocumentos.tsx).
      render: (a) => {
        const doc = documentos.find((d) => d.actuacion_id === a.id);
        return doc ? (
          <a
            href={`/api/documentos/${doc.id}/descargar`}
            className="font-medium text-secundario transition-colors hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            {doc.titulo}
          </a>
        ) : (
          "—"
        );
      },
    },
    {
      encabezado: "",
      render: (a) => (
        <button
          onClick={() => abrirEdicion(a)}
          className="text-sm font-medium text-secundario transition-colors hover:underline"
        >
          Editar
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <Boton tamano="sm" onClick={() => setModalAbierto(true)}>
          + Nueva Actuación
        </Boton>
      </div>
      <Tabla
        columnas={columnas}
        filas={actuaciones}
        claveFila={(a) => a.id}
        tituloVacio="Sin actuaciones registradas"
        descripcionVacio="Registrá presentaciones, audiencias o escritos."
      />
      <Modal
        titulo="Nueva actuación"
        abierto={modalAbierto}
        onCerrar={() => setModalAbierto(false)}
      >
        <form onSubmit={manejarEnvio} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Tipo" obligatorio>
              {tiposActuacion.length > 0 ? (
                <Select
                  required
                  value={valores.tipo}
                  onChange={(e) => actualizar("tipo", e.target.value)}
                >
                  <option value="">Seleccioná un tipo…</option>
                  {tiposActuacion.map((t) => (
                    <option key={t.id} value={t.nombre}>
                      {t.nombre}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  required
                  value={valores.tipo}
                  onChange={(e) => actualizar("tipo", e.target.value)}
                  placeholder="Presentación / Audiencia"
                />
              )}
            </Campo>
            <Campo etiqueta="Fecha" obligatorio>
              <Input
                type="date"
                required
                value={valores.fecha}
                onChange={(e) => actualizar("fecha", e.target.value)}
              />
            </Campo>
          </div>
          <Campo etiqueta="Descripción" obligatorio>
            <TextArea
              required
              rows={2}
              value={valores.descripcion}
              onChange={(e) => actualizar("descripcion", e.target.value)}
            />
          </Campo>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Prioridad">
              <Select
                value={valores.prioridad}
                onChange={(e) => actualizar("prioridad", e.target.value)}
              >
                <option value="Alta">Alta</option>
                <option value="Media">Media</option>
                <option value="Baja">Baja</option>
              </Select>
            </Campo>
            <Campo etiqueta="Responsable">
              {usuarios.length > 0 ? (
                <Select
                  value={valores.responsable_id}
                  onChange={(e) => actualizar("responsable_id", e.target.value)}
                >
                  {usuarios.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nombre_completo}
                    </option>
                  ))}
                </Select>
              ) : (
                <p className="flex h-[38px] items-center text-sm text-texto-secundario">
                  {usuario?.nombre_completo ?? "—"}
                </p>
              )}
            </Campo>
          </div>
          {mostrarDatosOcc && (
            <div className="space-y-4 rounded-lg border border-borde bg-fondo p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-texto-secundario">
                Datos adicionales (opcional) — OCC, notificaciones, pericias, oficios, testigos
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Campo etiqueta="Parte vinculada">
                  <Select value={parteId} onChange={(e) => setParteId(e.target.value)}>
                    <option value="">Sin vincular</option>
                    {partes.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.rol} — {p.nombre_completo}
                      </option>
                    ))}
                  </Select>
                </Campo>
                <Campo etiqueta="Lugar o enlace">
                  <Input value={lugarOEnlace} onChange={(e) => setLugarOEnlace(e.target.value)} placeholder="OCC / Zoom / dirección" />
                </Campo>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Campo etiqueta="Monto ofrecido">
                  <Input type="number" min={0} value={montoOfrecido} onChange={(e) => setMontoOfrecido(e.target.value)} />
                </Campo>
                <Campo etiqueta="Monto acordado">
                  <Input type="number" min={0} value={montoAcordado} onChange={(e) => setMontoAcordado(e.target.value)} />
                </Campo>
              </div>
            </div>
          )}
          <Campo etiqueta="Adjuntar documento">
            <input
              type="file"
              onChange={manejarArchivo}
              className="w-full text-sm text-texto-secundario file:mr-3 file:rounded-md file:border-0 file:bg-superficie file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-texto"
            />
          </Campo>

          <label className="flex items-center gap-2 text-sm font-medium text-texto">
            <input
              type="checkbox"
              checked={generarVencimiento}
              onChange={(e) => setGenerarVencimiento(e.target.checked)}
              className="h-4 w-4 rounded border-borde focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secundario focus-visible:ring-offset-2"
            />
            Generar vencimiento en Agenda
          </label>
          {generarVencimiento && (
            <div className="space-y-4 rounded-lg border border-borde bg-fondo p-3">
              <div className="grid grid-cols-2 gap-4">
                <Campo etiqueta="Fecha de vencimiento" obligatorio>
                  <Input
                    type="date"
                    required={generarVencimiento}
                    value={vencimiento.fecha_vencimiento}
                    onChange={(e) => setVencimiento((v) => ({ ...v, fecha_vencimiento: e.target.value }))}
                  />
                </Campo>
                <Campo etiqueta="Tipo de vencimiento" obligatorio>
                  <Input
                    required={generarVencimiento}
                    value={vencimiento.tipo_evento}
                    onChange={(e) => setVencimiento((v) => ({ ...v, tipo_evento: e.target.value }))}
                  />
                </Campo>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Campo etiqueta="Responsable del vencimiento">
                  {usuarios.length > 0 ? (
                    <Select
                      value={vencimiento.responsable_id}
                      onChange={(e) => setVencimiento((v) => ({ ...v, responsable_id: e.target.value }))}
                    >
                      {usuarios.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.nombre_completo}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <p className="flex h-[38px] items-center text-sm text-texto-secundario">
                      {usuario?.nombre_completo ?? "—"}
                    </p>
                  )}
                </Campo>
                <Campo etiqueta="Prioridad del vencimiento">
                  <Select
                    value={vencimiento.prioridad}
                    onChange={(e) => setVencimiento((v) => ({ ...v, prioridad: e.target.value }))}
                  >
                    <option value="Alta">Alta</option>
                    <option value="Media">Media</option>
                    <option value="Baja">Baja</option>
                  </Select>
                </Campo>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Boton
              type="button"
              variante="secundario"
              onClick={() => setModalAbierto(false)}
              disabled={guardando}
            >
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardando}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>

      <Modal titulo="Editar actuación" abierto={Boolean(enEdicion)} onCerrar={() => setEnEdicion(null)}>
        {enEdicion && (
          <form onSubmit={guardarEdicion} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Campo etiqueta="Tipo" obligatorio>
                {tiposActuacion.length > 0 ? (
                  <Select
                    required
                    value={enEdicion.tipo}
                    onChange={(e) => setEnEdicion((v) => v && { ...v, tipo: e.target.value })}
                  >
                    <option value="">Seleccioná un tipo…</option>
                    {tiposActuacion.map((t) => (
                      <option key={t.id} value={t.nombre}>
                        {t.nombre}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    required
                    value={enEdicion.tipo}
                    onChange={(e) => setEnEdicion((v) => v && { ...v, tipo: e.target.value })}
                  />
                )}
              </Campo>
              <Campo etiqueta="Fecha" obligatorio>
                <Input
                  type="date"
                  required
                  value={enEdicion.fecha}
                  onChange={(e) => setEnEdicion((v) => v && { ...v, fecha: e.target.value })}
                />
              </Campo>
            </div>
            <Campo etiqueta="Descripción" obligatorio>
              <TextArea
                required
                rows={2}
                value={enEdicion.descripcion}
                onChange={(e) => setEnEdicion((v) => v && { ...v, descripcion: e.target.value })}
              />
            </Campo>
            <div className="grid grid-cols-2 gap-4">
              <Campo etiqueta="Prioridad">
                <Select
                  value={enEdicion.prioridad}
                  onChange={(e) => setEnEdicion((v) => v && { ...v, prioridad: e.target.value as Actuacion["prioridad"] })}
                >
                  <option value="Alta">Alta</option>
                  <option value="Media">Media</option>
                  <option value="Baja">Baja</option>
                </Select>
              </Campo>
              <Campo etiqueta="Responsable">
                {(() => {
                  // Si GET /usuarios no está disponible para este rol (ver
                  // efecto de carga arriba), al menos se conserva la opción
                  // del responsable ya asignado — evita mostrar "—" para una
                  // actuación que sí tiene responsable.
                  const opciones =
                    usuarios.length > 0
                      ? usuarios
                      : enEdicion.responsable_id && enEdicion.responsable_nombre
                        ? [{ id: enEdicion.responsable_id, nombre_completo: enEdicion.responsable_nombre }]
                        : [];
                  return opciones.length > 0 ? (
                    <Select
                      value={enEdicion.responsable_id ?? ""}
                      onChange={(e) => setEnEdicion((v) => v && { ...v, responsable_id: e.target.value })}
                    >
                      <option value="">Sin asignar</option>
                      {opciones.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.nombre_completo}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <p className="flex h-[38px] items-center text-sm text-texto-secundario">—</p>
                  );
                })()}
              </Campo>
            </div>
            {mostrarDatosOccEdicion && (
              <div className="space-y-4 rounded-lg border border-borde bg-fondo p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-texto-secundario">
                  Datos adicionales — OCC, notificaciones, pericias, oficios, testigos
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <Campo etiqueta="Parte vinculada">
                    <Select
                      value={enEdicion.parte_id ?? ""}
                      onChange={(e) => setEnEdicion((v) => v && { ...v, parte_id: e.target.value })}
                    >
                      <option value="">Sin vincular</option>
                      {partes.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.rol} — {p.nombre_completo}
                        </option>
                      ))}
                    </Select>
                  </Campo>
                  <Campo etiqueta="Lugar o enlace">
                    <Input
                      value={enEdicion.lugar_o_enlace ?? ""}
                      onChange={(e) => setEnEdicion((v) => v && { ...v, lugar_o_enlace: e.target.value })}
                    />
                  </Campo>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Campo etiqueta="Monto ofrecido">
                    <Input
                      type="number"
                      min={0}
                      value={enEdicion.monto_ofrecido ?? ""}
                      onChange={(e) => setEnEdicion((v) => v && { ...v, monto_ofrecido: e.target.value ? Number(e.target.value) : null })}
                    />
                  </Campo>
                  <Campo etiqueta="Monto acordado">
                    <Input
                      type="number"
                      min={0}
                      value={enEdicion.monto_acordado ?? ""}
                      onChange={(e) => setEnEdicion((v) => v && { ...v, monto_acordado: e.target.value ? Number(e.target.value) : null })}
                    />
                  </Campo>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Boton type="button" variante="secundario" onClick={() => setEnEdicion(null)} disabled={guardandoEdicion}>
                Cancelar
              </Boton>
              <Boton type="submit" cargando={guardandoEdicion}>
                Guardar cambios
              </Boton>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
